import { logger } from "@/lib/logger";
/**
 * Lazy loading with retry for chunk-load failures
 * Handles "Failed to fetch dynamically imported module" errors
 * by retrying the import with exponential backoff
 */

import { lazy, ComponentType, LazyExoticComponent } from 'react';

interface RetryConfig {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  onRetry?: (attempt: number, error: Error) => void;
}

const DEFAULT_CONFIG: Required<Omit<RetryConfig, 'onRetry'>> = {
  maxRetries: 2,       // Reduced from 3 - fail faster
  initialDelay: 300,   // Reduced from 1000 - quicker first retry
  maxDelay: 2000,      // Reduced from 10000 - cap total wait time
};

/**
 * Check if an error is a chunk-load failure
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  
  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module') ||
    message.includes('failed to load') ||
    // Vite-specific errors
    message.includes('unable to preload css') ||
    message.includes('preload')
  );
}

/**
 * Retry an import with exponential backoff
 */
async function retryImport<T>(
  importFunc: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> {
  const { maxRetries, initialDelay, maxDelay } = { ...DEFAULT_CONFIG, ...config };
  
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        // Small delay before retry with exponential backoff
        const delay = Math.min(initialDelay * Math.pow(2, attempt - 1), maxDelay);
        await new Promise(resolve => setTimeout(resolve, delay));
        
        config.onRetry?.(attempt, lastError!);
        logger.warn(`[lazyWithRetry] Retry attempt ${attempt}/${maxRetries}`);
        
        // Cache-busting: Clear any cached module by creating a new import context
        // This forces the browser to re-fetch the module
        if ('caches' in window) {
          try {
            const keys = await caches.keys();
            for (const key of keys) {
              if (key.includes('workbox') || key.includes('vite')) {
                await caches.delete(key);
              }
            }
          } catch (e) {
            // Ignore cache clearing errors
          }
        }
      }
      
      return await importFunc();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // Only retry for chunk-load errors
      if (!isChunkLoadError(lastError)) {
        throw lastError;
      }
      
      // If this was the last attempt, throw
      if (attempt === maxRetries) {
        logger.error(`[lazyWithRetry] All ${maxRetries} retries failed`, lastError);
        throw lastError;
      }
    }
  }
  
  // This should never be reached, but TypeScript needs it
  throw lastError;
}

/**
 * Create a lazy component with automatic retry on chunk-load failures
 * 
 * @example
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 * 
 * // With custom config
 * const MyComponent = lazyWithRetry(
 *   () => import('./MyComponent'),
 *   { maxRetries: 5, initialDelay: 500 }
 * );
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  config?: RetryConfig
): LazyExoticComponent<T> {
  return lazy(() => retryImport(importFunc, config));
}

/**
 * Force a page refresh after chunk-load failures.
 * Clears build caches, unregisters the app service worker (a stale SW is the
 * most common cause of a "Page failed to load" dead-end after a deploy) and
 * reloads with a cache-busting query param.
 */
export async function forceRefreshOnChunkError(): Promise<void> {
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.allSettled(
        names
          .filter((n) => n.includes('workbox') || n.includes('vite') || n.includes('precache'))
          .map((n) => caches.delete(n))
      );
    }
  } catch {
    /* non-fatal */
  }

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.allSettled(
        regs
          .filter((r) => (r.active?.scriptURL || '').includes('/sw.js'))
          .map((r) => r.unregister())
      );
    }
  } catch {
    /* non-fatal */
  }

  // Cache-busted hard reload
  const url = new URL(window.location.href);
  url.searchParams.set('_r', Date.now().toString(36));
  window.location.replace(url.toString());
}

/** Session guard so auto-recovery never loops. */
const AUTO_RECOVER_KEY = 'ptc:chunk-auto-recover';

export function tryAutoRecoverFromChunkError(): boolean {
  try {
    if (sessionStorage.getItem(AUTO_RECOVER_KEY)) return false;
    sessionStorage.setItem(AUTO_RECOVER_KEY, '1');
  } catch {
    return false;
  }
  void forceRefreshOnChunkError();
  return true;
}

export function clearChunkAutoRecoverFlag(): void {
  try {
    sessionStorage.removeItem(AUTO_RECOVER_KEY);
  } catch {
    /* ignore */
  }
}


/**
 * Get user-friendly message for chunk-load errors
 */
export function getChunkErrorMessage(error: Error): {
  title: string;
  description: string;
  action: string;
} {
  return {
    title: 'Page failed to load',
    description: 'The page could not be loaded. This can happen with slow or unstable internet connections.',
    action: 'Reload Page'
  };
}
